import React from 'react';
import { Field, inputCls } from './shared';

export const USAGE_OPTIONS = [
  { key: 'internal', label: 'Internal use', hint: 'Used up on jobs' },
  { key: 'sale', label: 'For sale', hint: 'Sold at the counter' },
  { key: 'both', label: 'Both', hint: 'Used and sold' },
];
const GST_RATES = [0, 3, 5, 12, 18, 28, 40];
const blank = (v) => (v === null || v === undefined ? '' : v);

export const saleFormFrom = (item) => ({
  usage: item?.usage || 'internal',
  brand: item?.brand || '',
  hsn_code: item?.hsn_code || '',
  gst_rate: item?.gst_rate ?? 18,
  mrp: blank(item?.mrp),
  selling_price: blank(item?.selling_price),
});

export const isForSale = (usage) => usage === 'sale' || usage === 'both';

export const saleError = (form) => {
  if (!isForSale(form.usage)) return '';
  if (form.selling_price === '' || Number(form.selling_price) <= 0) return 'Add a selling price for anything marked for sale.';
  if (form.hsn_code && !/^(\d{4}|\d{6}|\d{8})$/.test(form.hsn_code.trim())) return 'HSN must be 4, 6 or 8 digits.';
  if (form.mrp !== '' && Number(form.selling_price) > Number(form.mrp)) return 'Selling price cannot be above the MRP.';
  return '';
};

export const salePayload = (form) => {
  const out = { usage: form.usage, brand: form.brand.trim(), hsn_code: form.hsn_code.trim() };
  ['gst_rate', 'mrp', 'selling_price'].forEach((k) => {
    if (form[k] !== '' && form[k] !== null) out[k] = Number(form[k]);
  });
  return out;
};

const SaleFields = ({ form, update }) => (
  <div className="space-y-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="sale-fields">
    <div>
      <span className="text-xs uppercase tracking-wider text-slate-400">How is this item used?</span>
      <div className="mt-1.5 grid grid-cols-3 gap-2" role="radiogroup">
        {USAGE_OPTIONS.map((o) => (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={form.usage === o.key}
            onClick={() => update('usage', o.key)}
            className={`rounded-lg border px-2 py-2 text-left transition-colors ${form.usage === o.key ? 'border-teal-400 bg-teal-500/15 text-teal-200' : 'border-white/10 text-slate-300 hover:bg-white/5'}`}
            data-testid={`form-usage-${o.key}`}
          >
            <div className="text-sm font-semibold">{o.label}</div>
            <div className="text-[11px] text-slate-400">{o.hint}</div>
          </button>
        ))}
      </div>
    </div>
    <Field label="Brand">
      <input value={form.brand} onChange={(e) => update('brand', e.target.value)} className={inputCls} placeholder="e.g. Bosch" data-testid="form-brand" />
    </Field>
    {isForSale(form.usage) && (
      <>
        <div className="grid grid-cols-2 gap-3">
          <Field label="HSN code">
            <input value={form.hsn_code} onChange={(e) => update('hsn_code', e.target.value.replace(/\D/g, '').slice(0, 8))} inputMode="numeric" className={`${inputCls} font-mono`} placeholder="e.g. 87083000" data-testid="form-hsn" />
          </Field>
          <Field label="GST %">
            <select value={form.gst_rate} onChange={(e) => update('gst_rate', e.target.value)} className={inputCls} data-testid="form-gst-rate">
              {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="MRP (₹)">
            <input type="number" min="0" step="0.01" value={form.mrp} onChange={(e) => update('mrp', e.target.value)} className={inputCls} data-testid="form-mrp" />
          </Field>
          <Field label="Selling price (₹, incl. GST)" required>
            <input type="number" min="0" step="0.01" value={form.selling_price} onChange={(e) => update('selling_price', e.target.value)} className={inputCls} data-testid="form-selling-price" />
          </Field>
        </div>
      </>
    )}
  </div>
);

export default SaleFields;
